import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { definirAcesso, limparAcesso, obter, requisitar } from '../lib/api.js';

/**
 * Sessão do usuário — Story 8.1 (RF-02/03).
 *
 * Context, e não uma store global: o estado é pequeno, tem um dono só e o ciclo
 * de vida dele é o da árvore. O que justificaria uma store — estado de UI
 * compartilhado entre telas distantes — ainda não existe (deve aparecer no
 * gerador de orçamento, Story 8.3).
 */

/**
 * Os papéis do sistema — espelha `PAPEIS` do backend.
 *
 * `gestor` entrou aqui na 1.6 porque a API passou a CRIAR gestores na 1.3: sem
 * ele o tipo mentia, e o header mostrava "Vendedor" para o dono da empresa.
 * Continua sendo cópia; movê-la para `packages/shared` é da 1.5, como o handoff
 * da 1.1 registrou.
 */
export type Papel = 'admin' | 'gestor' | 'vendedor';

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  role: Papel;
  tenantId: string;
  /**
   * A senha foi definida por outra pessoa — o app leva a pessoa para a troca
   * antes de liberar o resto (Story 1.4/1.6).
   *
   * Chega por TRÊS caminhos, e os três já respondem: `/login`, `/refresh`
   * (restauração após F5) e `/auth/eu`. É o `/refresh` que importa aqui — é
   * ele que o `AuthProvider` usa no boot, então a flag sobrevive ao reload sem
   * nenhuma chamada extra.
   */
  precisaTrocarSenha: boolean;
}

/** Sem `refreshToken`: ele vive no cookie `httpOnly` (Story 8.3). */
interface RespostaSessao {
  accessToken: string;
  usuario: Usuario;
}

interface Auth {
  usuario: Usuario | undefined;
  /** `true` enquanto tentamos restaurar a sessão no primeiro render. */
  restaurando: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  /**
   * Troca a própria senha e **mantém a pessoa logada**.
   *
   * A Story 1.4 revoga todas as sessões ao trocar — inclusive a que fez a
   * chamada — e emite uma nova na mesma resposta, exatamente como o login. Por
   * isso aqui aplicamos o token novo em vez de deslogar: sem isso a pessoa
   * seria expulsa pelo próprio ato de se proteger, e no primeiro acesso cairia
   * num laço login → trocar → deslogar → login.
   */
  trocarSenha: (senhaAtual: string, senhaNova: string) => Promise<void>;
  /** `revogado: false` ⇒ a sessão pode continuar válida no servidor. */
  sair: () => Promise<{ revogado: boolean }>;
  ehAdmin: boolean;
}

const AuthContext = createContext<Auth | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [usuario, setUsuario] = useState<Usuario | undefined>(undefined);
  const [restaurando, setRestaurando] = useState(true);

  /*
   * Recarregar a página zera o access token (ele vive em memória, de propósito).
   *
   * O cookie do refresh é `httpOnly`, então **não há como perguntar** se existe
   * sessão antes de tentar: a única forma de saber é chamar `/refresh` e ver o
   * que volta. Custa uma requisição no boot de quem não está logado — preço
   * justo por tirar o refresh do alcance do JavaScript.
   */
  useEffect(() => {
    let ativo = true;

    void (async () => {
      try {
        const r = await requisitar<RespostaSessao>('/api/auth/refresh', {
          metodo: 'POST',
          autenticar: false,
        });
        definirAcesso(r.dados.accessToken);
        if (ativo) setUsuario(r.dados.usuario);
      } catch {
        limparAcesso();
      } finally {
        if (ativo) setRestaurando(false);
      }
    })();

    return () => {
      ativo = false;
    };
  }, []);

  const entrar = useCallback(async (email: string, senha: string) => {
    const r = await requisitar<RespostaSessao>('/api/auth/login', {
      metodo: 'POST',
      corpo: { email, senha },
      autenticar: false,
    });
    // O refresh não vem aqui: o servidor o gravou no cookie.
    definirAcesso(r.dados.accessToken);
    setUsuario(r.dados.usuario);
  }, []);

  const trocarSenha = useCallback(async (senhaAtual: string, senhaNova: string) => {
    const r = await requisitar<RespostaSessao>('/api/auth/trocar-senha', {
      metodo: 'POST',
      corpo: { senhaAtual, senhaNova },
    });
    // Mesma forma da resposta do login: token novo em memória, refresh novo no
    // cookie (o servidor gravou), e o usuário já vem com a flag limpa.
    definirAcesso(r.dados.accessToken);
    setUsuario(r.dados.usuario);
  }, []);

  const sair = useCallback(async (): Promise<{ revogado: boolean }> => {
    let revogado = false;
    try {
      // Revoga no servidor e limpa o cookie. Sem isto o refresh continuaria
      // válido por 7 dias, mesmo depois de o usuário "sair".
      await requisitar('/api/auth/logout', { metodo: 'POST', autenticar: false });
      revogado = true;
    } catch {
      /*
       * Falhar ao avisar o servidor não pode prender o usuário na aplicação —
       * mas também não pode ser tratado como sucesso. A sessão local é
       * encerrada e quem chamou recebe `revogado: false` para poder avisar que
       * a sessão pode continuar ativa no servidor.
       */
    } finally {
      limparAcesso();
      setUsuario(undefined);
    }
    return { revogado };
  }, []);

  const valor = useMemo<Auth>(
    () => ({
      usuario,
      restaurando,
      entrar,
      trocarSenha,
      sair,
      ehAdmin: usuario?.role === 'admin',
    }),
    [usuario, restaurando, entrar, trocarSenha, sair],
  );

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  return ctx;
}

/**
 * Confere no servidor quem é o usuário — usado por diagnóstico.
 *
 * Traz `precisaTrocarSenha` lido do BANCO (Story 1.4), e não do token: o access
 * token dura 15 min e continuaria dizendo `true` depois da troca.
 */
export async function conferirSessao(): Promise<{
  id: string;
  role: Papel;
  precisaTrocarSenha: boolean;
}> {
  return obter<{ id: string; role: Papel; precisaTrocarSenha: boolean }>('/api/auth/eu');
}
